import { Duration, parse, serialize } from 'tinyduration';

import { formatDate, formatNumber } from '@angular/common';
import { UNLIMITED_TEXT } from './unlimited-value';

const SECONDS_PER_DAY = 86400;
type Seconds = number;
type Days = number;

/**
 * @returns Value in days
 */
export function secondsToDays(seconds: Seconds): Days {
  if (seconds === -1) {
    return -1;
  }
  return seconds / SECONDS_PER_DAY;
}

/**
 * @returns Value in days or null if seconds is null
 */
export function secondsToDaysOrNull(seconds: Seconds): Days | null {
  if (seconds === null) {
    return null;
  }
  return secondsToDays(seconds);
}
/**
 * @returns Value in seconds
 */
export function daysToSeconds(days: Days): Seconds {
  if (days === -1) {
    return -1;
  }
  return days * SECONDS_PER_DAY;
}

type FormatSecondsOptions = {
  /** Show hours if true */
  hours: boolean;
  /** Show minutes if true */
  minutes: boolean;
  /** Show seconds if true */
  seconds: boolean;
};

/**
 * Returns a string depicting time in the '0d 0h 0m 0s' format.
 * @param seconds Seconds integer
 * @param showMinutes IF true, minutes and seconds values are shown.
 */
export function formatSeconds(
  seconds: Seconds,
  options: FormatSecondsOptions = {
    hours: true,
    minutes: true,
    seconds: true,
  }
): string {
  const _days = Math.floor(seconds / (3600 * 24));
  const _hours = Math.floor((seconds % (3600 * 24)) / 3600);
  const _minutes = Math.floor((seconds % 3600) / 60);
  const _remainingSeconds = seconds % 60;

  let formattedTime = '';
  if (_days !== 0) {
    formattedTime += `${formatNumber(_days, 'en-US')}d`;
  }
  if (_hours !== 0 && options.hours) {
    formattedTime += ` ${formatNumber(_hours, 'en-US')}h`;
  }
  if (_minutes !== 0 && options.minutes) {
    formattedTime += ` ${formatNumber(_minutes, 'en-US')}m`;
  }
  if (_remainingSeconds !== 0 && options.seconds) {
    formattedTime += ` ${formatNumber(_remainingSeconds, 'en-US')}s`;
  }

  return formattedTime.trim();
}
/**
 * Returns a string depicting time in the '0d 0h 0m 0s' format.
 * @param seconds Seconds integer
 * @param property  property string.
 */
export function formatSecondsProperty(
  seconds: Seconds,
  property: string
): string {
  switch (property) {
    case 'serverSyncGracePeriod':
      if (seconds === 0 || seconds === -1) {
        return UNLIMITED_TEXT;
      } else {
        return formatSeconds(seconds);
      }
    case 'allowedClockOffset':
      if (seconds === -1) {
        return UNLIMITED_TEXT;
      } else {
        return formatSeconds(seconds);
      }
    case 'leaseDuration':
      if (seconds === -1 || seconds === 0) {
        return UNLIMITED_TEXT;
      } else {
        return formatSeconds(seconds);
      }
    default:
      return formatSeconds(seconds);
  }
}
/**
 * @returns Difference between two dates in seconds
 */
export function dateDifference(
  endDate: Date,
  startDate: Date = new Date()
): Seconds {
  return Math.floor((endDate.valueOf() - startDate.valueOf()) / 1000);
}

/**
 * @returns Date object from `days` ago
 */
export function getDateNDaysAgo(days: number): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate() - days);
}

/**
 * @returns Date object from `years` ago
 */
export function getDateNYearsAgo(years: number): Date {
  const now = new Date();
  return new Date(now.getFullYear() - years, now.getMonth(), now.getDate());
}
/**
 *
 * @param date date
 * @returns the next day date
 */
export function nextDayDate(date: string, daysToAdd = 1) {
  const tomorrow = new Date(date);
  tomorrow.setTime(tomorrow.getTime() + daysToAdd * SECONDS_PER_DAY * 1000);
  return tomorrow;
}
/** The Unix timestamp */
export type UnixTimestamp = number;

/**
 *
 * @param date
 * @returns midnight date in iso format
 */
export function getIsoMidnightDate(date: Date | string) {
  /**  Date selected in mat-calender is local, when converted to ISO as such, it is incorrect as 
  it accounts for time zone difference. Hence we take the date without time before converting it
  to ISO form.
  */
  return new Date(formatDate(date, 'yyyy-MM-dd', 'en-US')).toISOString();
}

/** Return a promise that resolves after a set time period*/
export function waitForSomeTime(ms = 1000) {
  return new Promise<void>((resolve) => {
    return setTimeout(() => {
      return resolve();
    }, ms);
  });
}
/**
 * Creates a valid ISO 8601 duration string from years, months, and days.
 * @param years - The number of years.
 * @param months - The number of months.
 * @param days - The number of days.
 * @param hours - The number of hours.
 * @param minutes - The number of minutes.
 * @param seconds - The number of seconds.
 * @returns A string representing the ISO 8601 duration.
 */
export function createISODuration(
  years: number,
  months: number,
  days: number,
  hours: number,
  minutes: number,
  seconds: number
): string {
  if (!years && !months && !days && !hours && !minutes && !seconds) {
    return '';
  }
  return serialize({
    years,
    months,
    days,
    hours,
    minutes,
    seconds,
  });
}

/**
 * Parses an ISO 8601 duration string and returns a formatted string
 * representing the duration in terms of years, months, and days.
 * @param isoDuration - The ISO 8601 duration string (e.g., "P1Y2M3D").
 * @returns A formatted string (e.g., "1 year 2 months 3 days 4 hours 5 minutes 6 seconds").
 */
export function formatISODuration(isoDuration: string): string {
  if (!isoDuration) {
    return '';
  }
  const duration: Duration = parse(isoDuration);
  const { years, months, days, hours, minutes, seconds } = duration;

  const parts = [];
  if (years) {
    parts.push(`${years} year${+years > 1 ? 's' : ''}`);
  }
  if (months) {
    parts.push(`${months} month${+months > 1 ? 's' : ''}`);
  }
  if (days) {
    parts.push(`${days} day${+days > 1 ? 's' : ''}`);
  }
  if (hours) {
    parts.push(`${hours} hour${+hours > 1 ? 's' : ''}`);
  }
  if (minutes) {
    parts.push(`${minutes} minute${+minutes > 1 ? 's' : ''}`);
  }
  if (seconds) {
    parts.push(`${seconds} second${+seconds > 1 ? 's' : ''}`);
  }

  return parts.join(' ').trim();
}

/**
 * Parses an ISO 8601 duration string and extracts the years, months, and days values.
 * @param isoDuration - The ISO 8601 duration string (e.g., "P1Y2M3D").
 * @returns An object containing the parsed years, months, and days as numbers or null if not present.
 */
export function parseISODuration(isoDuration: string): {
  years: number | null;
  months: number | null;
  days: number | null;
  hours: number | null;
  minutes: number | null;
  seconds: number | null;
} {
  if (!isoDuration) {
    return {
      years: null,
      months: null,
      days: null,
      hours: null,
      minutes: null,
      seconds: null,
    };
  }
  const duration: Duration = parse(isoDuration);
  return {
    years: duration.years ?? null,
    months: duration.months ?? null,
    days: duration.days ?? null,
    hours: duration.hours ?? null,
    minutes: duration.minutes ?? null,
    seconds: duration.seconds ?? null,
  };
}

/**
 * Converts a validity number to an ISO 8601 duration string.
 * @param validity - The validity number to convert.
 * @returns An object containing the parsed years, months, and days as numbers or null if not present.
 */
export function convertValidityToIsoDuration(validity: number | null): {
  years: number | null;
  months: number | null;
  days: number | null;
  hours: number | null;
  minutes: number | null;
  seconds: number | null;
} {
  if (!validity) {
    return {
      years: null,
      months: null,
      days: null,
      hours: null,
      minutes: null,
      seconds: null,
    };
  }

  // Convert validity from seconds to days and remaining seconds
  const days = Math.floor(validity / SECONDS_PER_DAY);
  const seconds = validity % SECONDS_PER_DAY;

  // Create a duration object with days and seconds
  const durationObj: Duration = {};

  if (days > 0) {
    durationObj.days = days;
  }

  if (seconds > 0) {
    durationObj.seconds = seconds;
  }

  return parseISODuration(serialize(durationObj));
}

/**
 * Checks if an ISO duration string has a value for the specified unit.
 * @param isoDuration - The ISO duration string to check
 * @param unit - The unit to check for ('years' | 'months' | 'days' | 'hours' | 'minutes' | 'seconds')
 * @returns True if the duration has a non-null value for the specified unit, false otherwise
 */
export function hasTimeUnit(
  isoDuration: string | null,
  unit: 'years' | 'months' | 'days' | 'hours' | 'minutes' | 'seconds'
): boolean {
  if (!isoDuration) {
    return false;
  }
  const parsedDuration = parseISODuration(isoDuration);
  return parsedDuration[unit] !== null;
}

/**
 * Returns the end of the day for a given date in ISO format.
 * @param date - The date to get the end of the day for.
 * @returns The end of the day for the given date in ISO format.
 */
export function endOfDay(date: string | null): string | null {
  if (!date) {
    return null;
  }
  const endOfDay = new Date(date);
  endOfDay.setHours(23, 59, 59, 999);
  return endOfDay.toISOString();
}
